import { useQuery } from "@tanstack/react-query"

import { graphql } from "#gql"
import type { InvoiceLateListQuery } from "#gql/graphql.ts"
import { queryKeys } from "#lib/query-keys.ts"
import { FleetListDocument } from "#pages/authenticated/fleet/page.tsx"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { client } from "#utils/graphql-client.ts"

import type { MonthSummary } from "../components/month-card.tsx"
import { LateInvoicesTable } from "./components/late-invoices-table.tsx"
import { LateKpis } from "./components/late-kpis.tsx"

export const InvoiceLateListDocument = graphql(/* GraphQL */ `
  query InvoiceLateList($input: QueryInvoiceLateListInput!) {
    invoiceLateList(input: $input) {
      id
      number
      vendor
      aircraftId
      currency
      total
      daysLate
      serviceDate {
        plaindate
        displaydate
      }
      invoiceDate {
        plaindate
        displaydate
      }
    }
  }
`)

export type LateInvoice = InvoiceLateListQuery["invoiceLateList"][number]

interface LateInvoicesProps {
  year: number
  aircraftId?: string
  search: string
  summaries: MonthSummary[]
  summariesLoading: boolean
}

export const LateInvoices = ({
  year,
  aircraftId,
  search,
  summaries,
  summariesLoading,
}: LateInvoicesProps) => {
  const { me } = useLoggedInUserInfo()
  const organizationId = me.organization.id

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.invoices.lateList(organizationId, aircraftId, year),
    queryFn: () =>
      client.request(InvoiceLateListDocument, {
        input: { organizationId, aircraftId, year },
      }),
  })
  const invoices = data?.invoiceLateList ?? []

  const { data: fleetData } = useQuery({
    queryKey: queryKeys.aircraft.list(organizationId),
    queryFn: () =>
      client.request(FleetListDocument, {
        input: { organizationId, sources: ["LEON", "MANUAL"] },
      }),
  })
  const tails = new Map(
    fleetData?.aircraftByOrganization.map(({ id, tail }) => [id, tail]) ?? [],
  )

  return (
    <div className="flex flex-col gap-4">
      <LateKpis
        invoices={invoices}
        isLoading={isLoading}
        summaries={summaries}
        summariesLoading={summariesLoading}
      />
      <LateInvoicesTable
        invoices={invoices}
        isLoading={isLoading}
        tails={tails}
        search={search}
        emptyText={`No late invoices in ${year}`}
      />
    </div>
  )
}
